/**
 * Disaster Recovery wizard — admin "panic button" for emergency restore.
 *
 * 4-step UI flow:
 *   1. Pick a pCloud backup (defaults to latest successful)
 *   2. Stage it into ``_dr_*`` shadow collections via the backend
 *   3. Review the per-collection diff
 *   4. Promote (Replace or Merge missing) with the explicit confirm token
 *
 * Author: Fabrizio CR 28/02/2026 — disaster recovery wizard.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, ArrowRight, RefreshCw, Trash2,
  Download, ShieldAlert, CheckCircle, XCircle, Database,
  PlayCircle, Cloud, History,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../config";

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; }
};

const AdminDisasterRecovery = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [selectedFileid, setSelectedFileid] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [session, setSession] = useState(null);
  const [confirmTokenExpected, setConfirmTokenExpected] = useState("");
  const [dbName, setDbName] = useState("");
  const [mode, setMode] = useState("merge_missing");
  const [confirmInput, setConfirmInput] = useState("");
  const [promoting, setPromoting] = useState(false);
  const [historicalSessions, setHistoricalSessions] = useState([]);

  // Load backups + token template at boot
  const loadBackups = useCallback(async () => {
    setBackupsLoading(true);
    try {
      const r = await axios.get(`${API}/backup/telcavoip-cloud/list?limit=50`);
      setBackups(r.data?.items || []);
    } catch (e) { toast.error("Lista backup pCloud fallita"); }
    finally { setBackupsLoading(false); }
  }, []);

  const loadToken = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/backup/dr/confirm-token-template`);
      setConfirmTokenExpected(r.data?.confirm_token || "");
      setDbName(r.data?.db_name || "");
    } catch (e) { /* not critical until step 4 */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/backup/dr/sessions?limit=10`);
      setHistoricalSessions(r.data?.items || []);
      const active = (r.data?.items || []).find((s) => s.status === "prepared");
      if (active && !session) {
        // Resume the active session — user came back to /admin/disaster-recovery
        setSession(active);
        setStep(3);
      }
    } catch (e) { /* not critical */ }
  }, [session]);

  useEffect(() => { loadBackups(); loadToken(); loadHistory(); }, [loadBackups, loadToken, loadHistory]);

  // ── Actions ────────────────────────────────────────────────────

  const startPrepare = async () => {
    if (!selectedFileid) return;
    setPreparing(true); setStep(2);
    try {
      const r = await axios.post(`${API}/backup/dr/prepare?fileid=${selectedFileid}`);
      setSession(r.data);
      setStep(3);
      toast.success("Sandbox preparato — rivedi il diff");
    } catch (e) {
      toast.error("Prepare fallito: " + (e.response?.data?.detail || e.message));
      setStep(1);
    } finally { setPreparing(false); }
  };

  const abortSession = async () => {
    if (!session) return;
    if (!window.confirm("Annullare la sessione DR? Le shadow collections verranno eliminate.")) return;
    try {
      await axios.delete(`${API}/backup/dr/sessions/${session.id}`);
      toast.success("Sessione annullata");
      setSession(null); setStep(1); setConfirmInput("");
      loadHistory();
    } catch (e) {
      toast.error("Abort fallito: " + (e.response?.data?.detail || e.message));
    }
  };

  const promoteSession = async () => {
    if (confirmInput.trim() !== confirmTokenExpected) {
      toast.error(`Token errato. Atteso esattamente: '${confirmTokenExpected}'`);
      return;
    }
    if (!window.confirm(
      `Promuovere la sessione DR in modalità ${mode.toUpperCase()}?\n\n` +
      "Verrà creato uno snapshot del DB attuale su pCloud prima del swap.\n" +
      "Questa operazione È IRREVERSIBILE (puoi solo ripristinare dallo snapshot)."
    )) return;
    setPromoting(true);
    try {
      const r = await axios.post(`${API}/backup/dr/sessions/${session.id}/promote`, {
        mode, confirm_token: confirmInput.trim(),
      });
      toast.success(`Promote completato. Snapshot pre-DR: ${r.data?.pre_dr_snapshot}`);
      setStep(5);  // Done screen
      setSession({ ...session, status: "promoted", swap_log: r.data?.swap_log, pre_dr_snapshot_path: r.data?.pre_dr_snapshot });
    } catch (e) {
      toast.error("Promote fallito: " + (e.response?.data?.detail || e.message));
    } finally { setPromoting(false); }
  };

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-lojack-black text-white p-6" data-testid="admin-dr-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/admin")} className="text-gray-400 hover:text-white flex items-center gap-2" data-testid="dr-back-to-dashboard">
            <ArrowLeft size={16} /> Torna al Dashboard
          </button>
          <span className="text-xs text-gray-500">DB: <code className="text-cyan-400">{dbName || "—"}</code></span>
        </div>

        <div className="bg-gradient-to-br from-red-950/40 to-transparent border border-red-500/30 rounded-2xl p-6 mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
            <ShieldAlert size={32} className="text-red-400" />
            🚨 Disaster Recovery
          </h1>
          <p className="text-red-200/80 text-sm">
            Wizard di ripristino di emergenza dal backup TelcaVoIP Cloud. Operazione critica:
            ogni promote crea automaticamente uno snapshot del DB attuale su pCloud prima del swap.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-6 px-2" data-testid="dr-stepper">
          {["Backup", "Sandbox", "Diff", "Promote", "Done"].map((label, idx) => {
            const num = idx + 1;
            const active = step >= num;
            const current = step === num;
            return (
              <div key={label} className="flex items-center flex-1">
                <div className={`flex flex-col items-center gap-1 ${active ? "text-white" : "text-gray-600"}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm transition ${current ? "bg-red-500 ring-4 ring-red-500/30" : active ? "bg-red-500/60" : "bg-gray-800 border border-gray-700"}`}>
                    {step > num ? <CheckCircle size={16} /> : num}
                  </div>
                  <span className="text-xs">{label}</span>
                </div>
                {num < 5 && <div className={`flex-1 h-px mx-2 ${step > num ? "bg-red-500/60" : "bg-gray-800"}`} />}
              </div>
            );
          })}
        </div>

        {/* ── Step 1: Pick backup ──────────────────────────────── */}
        {step === 1 && (
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-5" data-testid="dr-step1-pick">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2"><Cloud size={20} className="text-cyan-400" /> Scegli un backup pCloud</h2>
              <button onClick={loadBackups} disabled={backupsLoading} className="text-gray-400 hover:text-white">
                <RefreshCw size={14} className={backupsLoading ? "animate-spin" : ""} />
              </button>
            </div>
            {backups.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {backups.map((b) => {
                  const selected = selectedFileid === b.fileid;
                  return (
                    <label key={b.fileid} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition ${selected ? "border-red-500 bg-red-950/30" : "border-gray-800 bg-[#111] hover:border-gray-700"}`} data-testid={`dr-backup-${b.fileid}`}>
                      <div className="flex items-center gap-3">
                        <input type="radio" name="backup" value={b.fileid} checked={selected} onChange={() => setSelectedFileid(b.fileid)} className="accent-red-500" />
                        <div>
                          <p className="text-white text-sm">{b.name}</p>
                          <p className="text-gray-500 text-xs">{fmtDate(b.created)} · {fmtBytes(b.size_bytes)}</p>
                        </div>
                      </div>
                      <code className="text-gray-600 text-[10px]">fileid {b.fileid}</code>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">Nessun backup trovato nella cartella corrente.</p>
            )}
            <div className="flex justify-end mt-5">
              <button onClick={startPrepare} disabled={!selectedFileid || preparing} className="px-5 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg font-medium flex items-center gap-2" data-testid="dr-step1-next">
                Carica in sandbox <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Preparing ─────────────────────────────────── */}
        {step === 2 && (
          <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-12 text-center" data-testid="dr-step2-loading">
            <RefreshCw size={48} className="mx-auto text-cyan-400 animate-spin mb-4" />
            <h2 className="text-xl font-semibold mb-2">Caricamento sandbox in corso…</h2>
            <p className="text-gray-500 text-sm">Download del backup da pCloud e ripristino nelle shadow collections <code className="text-cyan-400">_dr_*</code>.</p>
          </div>
        )}

        {/* ── Step 3: Review diff ──────────────────────────────── */}
        {step === 3 && session && (
          <div className="space-y-4" data-testid="dr-step3-diff">
            <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-xl font-semibold flex items-center gap-2"><Database size={20} className="text-cyan-400" /> Diff vs DB attuale</h2>
                  <p className="text-gray-500 text-xs mt-1">Sandbox file: <code className="text-cyan-400">{session.filename}</code> · session <code className="text-gray-400">{session.id?.slice(0, 8)}</code></p>
                </div>
                <button onClick={abortSession} className="text-red-400 hover:text-red-300 text-sm inline-flex items-center gap-1" data-testid="dr-abort-btn">
                  <Trash2 size={14} /> Annulla sessione
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-[#111] rounded p-3"><p className="text-xs text-gray-500">Collezioni</p><p className="text-2xl font-bold">{session.diff?.summary?.collections_touched ?? 0}</p></div>
                <div className="bg-[#111] rounded p-3"><p className="text-xs text-gray-500">Righe nel backup</p><p className="text-2xl font-bold text-cyan-400">{session.diff?.summary?.total_backup_rows ?? 0}</p></div>
                <div className="bg-[#111] rounded p-3"><p className="text-xs text-gray-500">Righe in prod</p><p className="text-2xl font-bold text-emerald-400">{session.diff?.summary?.total_prod_rows ?? 0}</p></div>
              </div>
              <table className="w-full text-xs">
                <thead className="text-gray-500 border-b border-gray-800">
                  <tr><th className="text-left py-2">Collezione</th><th className="text-right">Backup</th><th className="text-right">Prod</th><th className="text-right">Delta</th><th className="text-right">Solo backup</th><th className="text-right">Solo prod</th></tr>
                </thead>
                <tbody>
                  {Object.entries(session.diff || {}).filter(([k]) => k !== "summary").map(([coll, d]) => (
                    <tr key={coll} className="border-b border-gray-900 hover:bg-[#111]" data-testid={`dr-diff-row-${coll}`}>
                      <td className="py-2 font-mono">{coll}</td>
                      <td className="text-right text-cyan-400">{d.backup_count}</td>
                      <td className="text-right text-emerald-400">{d.prod_count}</td>
                      <td className={`text-right ${d.delta > 0 ? "text-amber-400" : d.delta < 0 ? "text-red-400" : "text-gray-500"}`}>{d.delta > 0 ? `+${d.delta}` : d.delta}</td>
                      <td className="text-right text-gray-400">{d.ids_only_in_backup}</td>
                      <td className="text-right text-gray-400">{d.ids_only_in_prod}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between">
              <button onClick={abortSession} className="px-5 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm" data-testid="dr-step3-back">
                <ArrowLeft size={14} className="inline mr-1" /> Annulla
              </button>
              <button onClick={() => setStep(4)} className="px-5 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-medium" data-testid="dr-step3-next">
                Procedi al Promote <ArrowRight size={14} className="inline ml-1" />
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Promote ──────────────────────────────────── */}
        {step === 4 && session && (
          <div className="space-y-4" data-testid="dr-step4-promote">
            <div className="bg-red-950/30 border-2 border-red-500/50 rounded-xl p-5">
              <h2 className="text-xl font-bold text-red-300 flex items-center gap-2 mb-3"><AlertTriangle size={20} /> Promote — operazione distruttiva</h2>
              <ol className="text-sm text-red-200/80 space-y-1 list-decimal pl-5">
                <li>Verrà creato uno <strong>snapshot automatico del DB attuale</strong> su pCloud (rollback emergency)</li>
                <li>Le collezioni <code className="text-cyan-400">_dr_*</code> verranno applicate al DB live secondo la modalità scelta</li>
                <li>Le modifiche sono <strong>irreversibili</strong> (solo lo snapshot pre-DR permette il rollback)</li>
              </ol>
            </div>

            <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-5">
              <h3 className="font-semibold mb-3">Modalità</h3>
              <div className="space-y-2">
                <label className={`block p-3 rounded-lg border cursor-pointer ${mode === "merge_missing" ? "border-emerald-500 bg-emerald-950/20" : "border-gray-800"}`}>
                  <input type="radio" name="mode" value="merge_missing" checked={mode === "merge_missing"} onChange={(e) => setMode(e.target.value)} className="mr-2 accent-emerald-500" data-testid="dr-mode-merge" />
                  <strong>Merge missing only</strong> <span className="text-xs text-emerald-300 ml-1">(sicuro)</span>
                  <p className="text-xs text-gray-500 mt-1 ml-5">Inserisce SOLO i record presenti nel backup ma assenti dal DB live. Nessun update, nessun delete.</p>
                </label>
                <label className={`block p-3 rounded-lg border cursor-pointer ${mode === "replace" ? "border-red-500 bg-red-950/20" : "border-gray-800"}`}>
                  <input type="radio" name="mode" value="replace" checked={mode === "replace"} onChange={(e) => setMode(e.target.value)} className="mr-2 accent-red-500" data-testid="dr-mode-replace" />
                  <strong>Replace</strong> <span className="text-xs text-red-300 ml-1">(distruttivo)</span>
                  <p className="text-xs text-gray-500 mt-1 ml-5">Drop delle collezioni live e rename delle <code>_dr_*</code>. Ogni dato post-backup viene perso.</p>
                </label>
              </div>
            </div>

            <div className="bg-[#0a0a0a] border border-amber-500/40 rounded-xl p-5">
              <h3 className="font-semibold text-amber-300 mb-2">Conferma esplicita</h3>
              <p className="text-xs text-gray-400 mb-3">Digita esattamente questa stringa per confermare:</p>
              <div className="bg-black border border-amber-500/30 rounded p-3 mb-3">
                <code className="text-amber-300 select-all font-mono text-sm" data-testid="dr-confirm-template">{confirmTokenExpected}</code>
              </div>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder="Digita la stringa qui sopra…"
                className="w-full px-4 py-2.5 bg-[#111] border border-gray-700 rounded-lg text-white font-mono"
                data-testid="dr-confirm-input"
              />
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(3)} className="px-5 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm" data-testid="dr-step4-back">
                <ArrowLeft size={14} className="inline mr-1" /> Torna al diff
              </button>
              <button
                onClick={promoteSession}
                disabled={promoting || confirmInput.trim() !== confirmTokenExpected}
                className="px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg font-bold flex items-center gap-2"
                data-testid="dr-step4-promote"
              >
                {promoting ? <RefreshCw size={16} className="animate-spin" /> : <PlayCircle size={16} />}
                {promoting ? "Promote in corso…" : "PROMOTE"}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 5: Done ──────────────────────────────────────── */}
        {step === 5 && session && (
          <div className="bg-emerald-950/30 border border-emerald-500/40 rounded-xl p-8 text-center" data-testid="dr-step5-done">
            <CheckCircle size={64} className="mx-auto text-emerald-400 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Promote completato</h2>
            <p className="text-emerald-200/80 mb-4">Il DB live è stato ripristinato in modalità <code>{session.promote_mode || mode}</code>.</p>
            {session.pre_dr_snapshot_path && (
              <p className="text-xs text-gray-500 mt-2">
                Snapshot pre-DR per rollback: <br />
                <code className="text-amber-400 text-[10px] break-all">{session.pre_dr_snapshot_path}</code>
              </p>
            )}
            {session.swap_log && Object.keys(session.swap_log).length > 0 && (
              <div className="text-left bg-[#0a0a0a] rounded p-3 mt-4 text-xs max-w-md mx-auto">
                {Object.entries(session.swap_log).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1 border-b border-gray-800 last:border-0">
                    <code className="text-cyan-400">{k}</code>
                    <span className="text-gray-400">
                      {v.error ? <span className="text-red-400">ERR</span>
                        : v.mode === "replace" ? `${v.before} → ${v.after}`
                        : v.mode === "merge_missing" ? `+${v.inserted}`
                        : JSON.stringify(v)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => navigate("/admin")} className="mt-6 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-medium" data-testid="dr-back-after-done">
              Torna al Dashboard
            </button>
          </div>
        )}

        {/* Historical sessions sidebar */}
        {historicalSessions.length > 0 && (
          <div className="mt-8 bg-[#0a0a0a] border border-gray-800 rounded-xl p-4" data-testid="dr-history">
            <h3 className="text-sm font-semibold flex items-center gap-2 mb-3"><History size={14} /> Storico sessioni DR</h3>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {historicalSessions.map((s) => (
                <div key={s.id} className="flex items-center justify-between bg-[#111] rounded px-3 py-1.5 text-xs">
                  <code className="text-gray-400">{s.id?.slice(0, 8)}</code>
                  <span className="text-gray-500">{fmtDate(s.created_at)}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${s.status === "promoted" ? "bg-emerald-500/20 text-emerald-300" : s.status === "aborted" ? "bg-gray-500/20 text-gray-300" : "bg-amber-500/20 text-amber-300"}`}>{s.status}</span>
                  <span className="text-gray-500">{s.created_by}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDisasterRecovery;
